import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type TargetType = 'all' | 'tier' | 'goal' | 'experience' | 'specific';
export type ScheduledNotificationType = 'weigh_in' | 'motivation' | 'training_reminder';

export interface PushNotificationPayload {
  title: string;
  message: string;
  url?: string;
  targetType: TargetType;
  targetValue?: string; // tier name, goal, or user IDs
  userIds?: string[];
}

export interface PushNotificationLog {
  id: string;
  sent_by: string | null;
  recipient_user_ids: string[] | null;
  target_type: string;
  target_filter: Record<string, unknown> | null;
  notification_type: string;
  title: string;
  message: string;
  url: string | null;
  onesignal_response: Record<string, unknown> | null;
  created_at: string;
}

export function useAdminPushNotifications() {
  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState<PushNotificationLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [testingSending, setTestingSending] = useState(false);

  const sendNotification = useCallback(async (payload: PushNotificationPayload) => {
    setSending(true);
    try {
      // Build the request payload for send-push-notification edge function
      const requestPayload: Record<string, unknown> = {
        title: payload.title,
        message: payload.message,
      };

      if (payload.url) {
        requestPayload.url = payload.url;
      }

      // Set targeting based on targetType
      if (payload.targetType === 'all') {
        requestPayload.sendToAll = true;
      } else if (payload.targetType === 'specific' && payload.userIds?.length) {
        requestPayload.userIds = payload.userIds;
      } else if (payload.targetType === 'tier' && payload.targetValue) {
        requestPayload.filters = [{
          field: 'tag',
          key: 'tier',
          value: payload.targetValue,
          relation: '=',
        }];
      } else if (payload.targetType === 'goal' && payload.targetValue) {
        requestPayload.filters = [{
          field: 'tag',
          key: 'goal',
          value: payload.targetValue,
          relation: '=',
        }];
      } else if (payload.targetType === 'experience' && payload.targetValue) {
        requestPayload.filters = [{
          field: 'tag',
          key: 'experience',
          value: payload.targetValue,
          relation: '=',
        }];
      }

      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: requestPayload,
      });

      if (error) throw error;

      // Log the notification
      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase.from('push_notification_logs').insert({
        sent_by: user?.id,
        target_type: payload.targetType,
        target_filter: payload.targetValue ? { value: payload.targetValue } : null,
        notification_type: 'admin',
        title: payload.title,
        message: payload.message,
        url: payload.url,
        onesignal_response: data,
        recipient_user_ids: payload.userIds || null,
      });

      return { success: true, data };
    } catch (error) {
      console.error('Error sending push notification:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to send notification' 
      };
    } finally {
      setSending(false);
    }
  }, []);

  const triggerScheduledNotification = useCallback(async (type: ScheduledNotificationType) => {
    setTestingSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('scheduled-notifications', {
        body: { type },
      });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error triggering scheduled notification:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to trigger notification' 
      };
    } finally {
      setTestingSending(false);
    }
  }, []);

  const fetchLogs = useCallback(async (limit = 20) => {
    setLogsLoading(true);
    try {
      const { data, error } = await supabase
        .from('push_notification_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      
      // Type assertion since we know the structure
      setLogs((data || []) as unknown as PushNotificationLog[]);
    } catch (error) {
      console.error('Error fetching notification logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  return {
    sendNotification,
    sending,
    logs,
    logsLoading,
    fetchLogs,
    triggerScheduledNotification,
    testingSending,
  };
}
